import * as React from 'react';

import { BaseComponent } from './BaseComponent';
import { ColorRGB } from '../utils/GlobalHelper';
import { CoreColorPickerButton } from './CoreColorPickerButton';

interface CoreColorPickerProps extends React.Props<CoreColorPicker> {
    height: number;
    widthPan: number;
    spaceWidth: number;
    widthSlider: number;
    initialValue: ColorRGB;
    defaultColors: Array<ColorRGB>;
    onChange: (value: ColorRGB | null) => void;
}

interface CoreColorPickerState {
    hueX: number;
    hueY: number;
    alphaY: number;
    activeColor: ColorRGB | null;
}

export class CoreColorPicker extends BaseComponent<CoreColorPickerProps, CoreColorPickerState> {
    protected hueCanvas: HTMLCanvasElement;
    protected hueDiv: HTMLDivElement;
    protected alphaCanvas: HTMLCanvasElement;
    protected alphaDiv: HTMLDivElement;

    public componentWillMount(): void {
        this.setColor(this.props.initialValue);
    }

    public componentDidMount(): void {
        this.alphaCanvas.width = this.props.widthSlider;
        this.alphaCanvas.height = this.props.height;
        this.hueCanvas.width = this.props.widthPan;
        this.hueCanvas.height = this.props.height;

        this.drawHue();

        const ctx: CanvasRenderingContext2D | null = this.hueCanvas.getContext('2d');
        if (ctx == null) {
            return;
        }

        const rgbAtPixel: Uint8ClampedArray = ctx.getImageData(this.state.hueX, this.state.hueY, 1, 1).data;
        this.drawAlpha({r: rgbAtPixel[0], g: rgbAtPixel[1], b: rgbAtPixel[2]});
        this.bindEvents();
    }

    public componentDidUpdate(): void {
        if (this.state != null && this.alphaCanvas != null && this.hueCanvas != null) {
            const ctx: CanvasRenderingContext2D | null = this.hueCanvas.getContext('2d');
            if (ctx == null) {
                return;
            }

            const rgbAtPixel: Uint8ClampedArray = ctx.getImageData(this.state.hueX, this.state.hueY, 1, 1).data;
            this.drawAlpha({r: rgbAtPixel[0], g: rgbAtPixel[1], b: rgbAtPixel[2]});
        }
    }

    public componentWillUnmount(): void {
        this.unbindEvents();
    }

    public render(): JSX.Element {
        const hueStyle: React.CSSProperties = {
            width: this.props.widthPan,
            height: this.props.height
        };

        const componentStyle: React.CSSProperties = {
            width: this.props.widthPan + this.props.spaceWidth + this.props.widthSlider,
            height: this.props.height,
        };

        if (this.props.defaultColors.length) {
            (componentStyle.height as number) += this.props.spaceWidth + 20;
        }

        return (
            <div style={componentStyle} className="core-color-picker">
                <canvas
                    ref={(node: HTMLCanvasElement) => { this.hueCanvas = node; }}
                    className="core-color-picker-hue-canvas"
                    style={hueStyle}
                />
                <div
                    ref={(node: HTMLDivElement) => { this.hueDiv = node; }}
                    className="core-color-picker-hue-area"
                    style={hueStyle}
                >
                    <div
                        className="core-color-picker-crosshair"
                        style={{left: this.state.hueX - 1, top: this.state.hueY - 1}}
                    />
                </div>

                <canvas
                    ref={(node: HTMLCanvasElement) => { this.alphaCanvas = node; }}
                    className="core-color-picker-alpha-canvas"
                    style={{
                        width: this.props.widthSlider,
                        height: this.props.height
                    }}
                />
                <div
                    className="core-color-picker-alpha-div"
                    style={{
                        width: this.props.widthSlider,
                        height: this.props.height
                    }}
                >
                    <div
                        className="core-colorpicker-thumb"
                        style={{top: this.state.alphaY}}
                    />
                </div>
                <div
                    ref={(node: HTMLDivElement) => { this.alphaDiv = node; }}
                    className="core-color-picker-alpha-area"
                    style={{
                        right: -this.props.spaceWidth,
                        width: this.props.widthSlider + 2 * this.props.spaceWidth,
                        height: this.props.height
                    }}
                />
                <div className="core-color-picker-buttons">
                    {this.props.defaultColors.map(this.getColorButton)}
                </div>
            </div>
        );
    }

    protected getColorButton = (defaultColor: ColorRGB, index: number) => {
        return (
            <CoreColorPickerButton
                key={index}
                buttonColor={defaultColor}
                activeColor={this.state.activeColor}
                onClick={this.onColorClick}
            />
        );
    };

    protected onColorClick = (value: ColorRGB) => {
        this.setColor(value);
        this.props.onChange(value);
    };

    protected RGB_HSV(color: ColorRGB): Array<number> {
        const colorCopy: ColorRGB = {...color};
        colorCopy.r /= 255;
        colorCopy.g /= 255;
        colorCopy.b /= 255;
        const n: number = Math.min(Math.min(colorCopy.r, colorCopy.g), colorCopy.b);
        const v: number = Math.max(Math.max(colorCopy.r, colorCopy.g), colorCopy.b);
        const m: number = v - n;
        if (m === 0) {
            return [0, 0, 100 * v ];
        }

        let h: number;

        if (colorCopy.r === n) {
            h = 3 + (colorCopy.b - colorCopy.g) / m;
        } else if (colorCopy.g === n) {
            h = 5 + (colorCopy.r - colorCopy.b) / m;
        } else {
            h = 1 + (colorCopy.g - colorCopy.r) / m;
        }

        return [
            60 * (h === 6 ? 0 : h),
            100 * (m / v),
            100 * v
        ];
    }

    protected HSV_RGB(h: number, s: number, v: number): ColorRGB | undefined {
        const u: number = 255 * (v / 100);

        if (h === null) {
            return {r: u, g: u, b: u};
        }

        h /= 60;
        s /= 100;

        const i: number = Math.floor(h);
        const f: number = i % 2 ? h - i : 1 - (h - i);
        const m: number = u * (1 - s);
        const n: number = u * (1 - s * f);
        switch (i) {
            case 6:
            case 0: return {r: u, g: n, b: m};
            case 1: return {r: n, g: u, b: m};
            case 2: return {r: m, g: u, b: n};
            case 3: return {r: m, g: n, b: u};
            case 4: return {r: n, g: m, b: u};
            case 5: return {r: u, g: m, b: n};
            default:
                return undefined;
        }
    }

    protected drawHue = (): void => {
        if (this.hueCanvas == null) {
            return;
        }

        const ctx: CanvasRenderingContext2D | null = this.hueCanvas.getContext('2d');
        if (ctx == null) {
            return;
        }

        ctx.clearRect(0, 0, this.hueCanvas.width, this.hueCanvas.height);

        const hGrad: CanvasGradient = ctx.createLinearGradient(0, 0, this.hueCanvas.width, 0);
        hGrad.addColorStop(0 / 6, '#F00');
        hGrad.addColorStop(1 / 6, '#FF0');
        hGrad.addColorStop(2 / 6, '#0F0');
        hGrad.addColorStop(3 / 6, '#0FF');
        hGrad.addColorStop(4 / 6, '#00F');
        hGrad.addColorStop(5 / 6, '#F0F');
        hGrad.addColorStop(6 / 6, '#F00');
        ctx.fillStyle = hGrad;
        ctx.fillRect(0, 0, this.hueCanvas.width, this.hueCanvas.height);

        const vGrad: CanvasGradient = ctx.createLinearGradient(0, 0, 0, this.hueCanvas.height);
        vGrad.addColorStop(0, 'rgba(254, 254, 254, 0)');
        vGrad.addColorStop(1, 'rgba(254, 254, 254, 1)');
        ctx.fillStyle = vGrad;
        ctx.fillRect(0, 0, this.hueCanvas.width, this.hueCanvas.height);
        ctx.restore();
    };

    protected getColor = (): ColorRGB | null => {
        if (this.alphaCanvas == null) {
            return null;
        }

        const ctx: CanvasRenderingContext2D | null = this.alphaCanvas.getContext('2d');
        if (ctx == null) {
            return null;
        }

        const rgbAtPixel: Uint8ClampedArray = ctx.getImageData(
            .5 * this.props.widthSlider,
            (this.state.alphaY),
            1,
            1
        ).data;
        return ({r: rgbAtPixel[0], g: rgbAtPixel[1], b: rgbAtPixel[2]});
    };

    protected drawAlpha = (currentColor: ColorRGB): void => {
        if (this.alphaCanvas == null) {
            return;
        }

        const ctx: CanvasRenderingContext2D | null = this.alphaCanvas.getContext('2d');
        if (ctx == null) {
            return;
        }
        ctx.clearRect(0, 0, this.alphaCanvas.width, this.alphaCanvas.height);
        const color1: string = (
            'rgb(' +
            currentColor.r + ',' +
            currentColor.g + ',' +
            currentColor.b + ')'
        );
        const color2: string = '#000';

        const vGrad: CanvasGradient = ctx.createLinearGradient(0, 0, 0, this.alphaCanvas.height);
        vGrad.addColorStop(0 / 1, color1);
        vGrad.addColorStop(1 / 1, color2);
        ctx.fillStyle = vGrad;
        ctx.fillRect(0, 0, this.alphaCanvas.width, this.alphaCanvas.height);
    };

    protected pointerEvent = (pointerEvent: PointerEvent): void => {
        const rect: ClientRect = this.hueCanvas.getBoundingClientRect();
        if (pointerEvent.buttons === 1) {
            const pickerState: CoreColorPickerState = jQuery.extend({}, this.state, {
                hueX: Math.min(Math.max(pointerEvent.pageX - rect.left, 0), this.props.widthPan - 1),
                hueY: Math.min(Math.max(pointerEvent.pageY - rect.top, 0), this.props.height - 1)
            });

            const ctx: CanvasRenderingContext2D | null = this.hueCanvas.getContext('2d');
            if (ctx == null) {
                return;
            }

            const rgbAtPixel: Uint8ClampedArray = ctx.getImageData(pickerState.hueX, pickerState.hueY, 1, 1).data;
            this.drawAlpha({r: rgbAtPixel[0], g: rgbAtPixel[1], b: rgbAtPixel[2]});
            this.setState({...pickerState, activeColor: this.getColor()});
            this.props.onChange(this.getColor());
        }
    };

    protected pointerEventAlpha = (pointerEvent: PointerEvent): void => {
        const rect: ClientRect = this.alphaCanvas.getBoundingClientRect();
        if (pointerEvent.buttons === 1) {
            const pickerState: CoreColorPickerState = jQuery.extend({}, this.state, {
                alphaY: Math.min(Math.max(pointerEvent.pageY - rect.top, 0), this.props.height - 1)
            });
            this.setState({...pickerState, activeColor: this.getColor()});
            this.props.onChange(this.getColor());
        }
    };

    protected bindEvents = (): void => {
        this.hueDiv.setAttribute('touch-action', 'none');
        this.hueDiv.addEventListener('pointermove', this.pointerEvent, true);
        this.hueDiv.addEventListener('pointerdown', this.pointerEvent, true);

        this.alphaDiv.setAttribute('touch-action', 'none');
        this.alphaDiv.addEventListener('pointermove', this.pointerEventAlpha, true);
        this.alphaDiv.addEventListener('pointerdown', this.pointerEventAlpha, true);
    };

    protected unbindEvents = (): void => {
        this.hueDiv.removeEventListener('pointermove', this.pointerEvent, true);
        this.hueDiv.removeEventListener('pointerdown', this.pointerEvent, true);

        this.alphaDiv.removeEventListener('pointermove', this.pointerEventAlpha, true);
        this.alphaDiv.removeEventListener('pointerdown', this.pointerEventAlpha, true);
    };

    protected setColor = (value: ColorRGB): void => {
        const hsv: Array<number> = this.RGB_HSV(value);
        const x: number = Math.round((hsv[0] / 360) * (this.props.widthPan - 1));
        const y: number = Math.round((1 - hsv[1] / 100) * (this.props.height - 1));
        const alphaY: number = Math.round((1 - hsv[2] / 100) * (this.props.height - 1));
        this.setState({hueX: x, hueY: y, alphaY: alphaY, activeColor: value});
    };
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/CoreColorPicker.tsx